import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth.tsx';

function FullScreenLoader() {
    return (
        <div className="flex min-h-dvh items-center justify-center">
            <span className="loading loading-ring loading-lg text-primary" />
        </div>
    );
}

/** Exige une session. Redirige vers la connexion en mémorisant la
 *  destination, pour y revenir une fois connecté. */
export function RequireAuth({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return <FullScreenLoader />;
    if (!user) return <Navigate to="/connexion" state={{ from: location.pathname }} replace />;

    // Un compte dont le mot de passe est imposé (premier administrateur créé
    // par le seeder) ne va nulle part ailleurs tant qu'il n'en a pas changé.
    if (user.mustChangePassword && location.pathname !== '/mot-de-passe') {
        return <Navigate to="/mot-de-passe" replace />;
    }

    return <>{children}</>;
}

/** Exige le rôle administrateur.
 *
 *  Barrière d'interface, pas barrière de sécurité : elle évite d'afficher un
 *  écran vide à quelqu'un qui n'y a pas droit. Ce qui protège réellement les
 *  données, c'est le contrôle de rôle fait par le serveur sur chaque route
 *  /api/admin — le front est du code que l'utilisateur contrôle. */
export function RequireAdmin({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();

    if (loading) return <FullScreenLoader />;
    if (!user) return <Navigate to="/connexion" replace />;
    if (user.role !== 'admin') {
        return (
            <div className="alert alert-error mt-6">
                <span>Cette section est réservée aux administrateurs.</span>
            </div>
        );
    }
    return <>{children}</>;
}
